import { useCallback, useState, useEffect, useRef } from 'react';
import type { SessionConfig } from '../types';
import { getSessionId } from '../utils/session';
import { notifyHost } from '../utils/notifyHost';

export type AnalyticsTransport = 'fetch' | 'beacon';

export interface AnalyticsEvent {
  action: string;
  payload: Record<string, unknown>;
  transport: AnalyticsTransport;
}

interface UseAnalyticsOptions {
  analyticsBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  customFields: Record<string, unknown>;
  analyticsContext?: Record<string, unknown>;
  analyticsEnabled?: boolean;
  analyticsSampleRate?: number;
  analyticsEndpoint?: string | null;
  analyticsApiKey?: string | null;
  sessionConfig?: SessionConfig;
  onAnalyticsEvent?: (event: AnalyticsEvent) => void;
}

interface AnalyticsState {
  sessionId: string;
  sessionStartTime: number;
  pageViews: number;
  focusCount: number;
  eventsLogged: number;
  lastActivity: number;
}

interface EventData {
  duration?: number;
  toolCalls?: number;
  citations?: number;
  error?: string;
  characterCount?: number;
  eventId?: string;
  idempotencyKey?: string;
  important?: boolean;
  [key: string]: unknown;
}

interface QueuedEvent {
  endpoint: string;
  body: string;
  headers: Record<string, string>;
  attempts: number;
  nextAttemptAt: number;
}

const MAX_QUEUE_SIZE = 50;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const UNSAMPLED_ANALYTICS_ACTIONS = new Set([
  'outcome',
  'cart_added',
  'checkout_redirect',
  'checkout_started',
  'purchase.completed',
  'order.converted',
  'subscription_action',
  'subscription.started',
  'subscription.updated',
  'subscription.cancelled',
  'subscription.skipped',
  'subscription.rescheduled',
  'subscription.frequency_changed',
  'handoff_request',
  'handoff_created',
  'handoff_failed',
  'handoff_message_sent',
]);

function createAnalyticsEventId(action: string, sessionId: string, eventIndex: number) {
  const normalizedAction = action.replace(/[^a-z0-9_.:-]+/gi, '_').slice(0, 80) || 'event';
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `rw_${normalizedAction}_${crypto.randomUUID()}`;
  }
  return `rw_${normalizedAction}_${sessionId || 'session'}_${eventIndex}_${Date.now()}`;
}

function isImportantAnalyticsEvent(action: string, data: Record<string, unknown>) {
  if (data.important === true) return true;
  if (UNSAMPLED_ANALYTICS_ACTIONS.has(action)) return true;
  const outcome = typeof data.outcome === 'string' ? data.outcome.toLowerCase() : '';
  const funnelStage = typeof data.funnelStage === 'string' ? data.funnelStage : '';
  return (
    funnelStage === 'cart_created' ||
    funnelStage === 'checkout_started' ||
    funnelStage === 'converted_order' ||
    funnelStage === 'subscription_action' ||
    outcome.includes('purchase') ||
    outcome.includes('checkout') ||
    outcome.includes('cart') ||
    outcome.includes('subscription') ||
    outcome.includes('order.converted')
  );
}

export function useAnalytics({
  analyticsBaseUrl,
  apiKey,
  customFields,
  analyticsContext,
  analyticsEnabled,
  analyticsSampleRate,
  analyticsEndpoint,
  analyticsApiKey,
  sessionConfig,
  onAnalyticsEvent,
}: UseAnalyticsOptions) {
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>(() => {
    const sessionId = getSessionId(sessionConfig) || '';
    const initialUrl = typeof window !== 'undefined' ? window.location.href : '';
    return {
      sessionId,
      sessionStartTime: Date.now(),
      pageViews: initialUrl ? 1 : 0,
      focusCount: 0,
      eventsLogged: 0,
      lastActivity: Date.now(),
    };
  });
  const analyticsStateRef = useRef(analyticsState);
  const eventIndexRef = useRef(analyticsState.eventsLogged);
  const pageViewsRef = useRef(analyticsState.pageViews);
  const focusCountRef = useRef(analyticsState.focusCount);
  const lastActivityRef = useRef(analyticsState.lastActivity);
  const seenUrlsRef = useRef<Set<string>>(new Set());
  const queueRef = useRef<QueuedEvent[]>([]);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onAnalyticsEventRef = useRef(onAnalyticsEvent);

  useEffect(() => {
    onAnalyticsEventRef.current = onAnalyticsEvent;
  }, [onAnalyticsEvent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    seenUrlsRef.current.add(window.location.href);
  }, []);

  const sampleRef = useRef<boolean>(true);
  useEffect(() => {
    if (typeof analyticsSampleRate !== 'number') {
      sampleRef.current = true;
      return;
    }
    const rate = Math.max(0, Math.min(1, analyticsSampleRate));
    sampleRef.current = Math.random() <= rate;
  }, [analyticsSampleRate]);

  const resolveEndpoint = useCallback(() => {
    if (!analyticsBaseUrl) return null;
    const trimmed = analyticsBaseUrl.replace(/\/$/, '');
    if (analyticsEndpoint) {
      if (analyticsEndpoint.startsWith('http')) return analyticsEndpoint;
      return `${trimmed}${analyticsEndpoint.startsWith('/') ? '' : '/'}${analyticsEndpoint}`;
    }
    if (trimmed.endsWith('/analytics') || trimmed.endsWith('/events')) return trimmed;
    if (trimmed.includes('/chat-analytics')) return `${trimmed}/events`;
    return `${trimmed}/analytics`;
  }, [analyticsBaseUrl, analyticsEndpoint]);

  useEffect(() => {
    analyticsStateRef.current = analyticsState;
    pageViewsRef.current = analyticsState.pageViews;
    focusCountRef.current = analyticsState.focusCount;
    lastActivityRef.current = analyticsState.lastActivity;
    eventIndexRef.current = Math.max(eventIndexRef.current, analyticsState.eventsLogged);
  }, [analyticsState]);

  const recordUrlIfNew = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    const url = window.location.href;
    if (seenUrlsRef.current.has(url)) return false;
    seenUrlsRef.current.add(url);
    pageViewsRef.current += 1;
    return true;
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      focusCountRef.current += 1;
      lastActivityRef.current = now;
      const newUrl = recordUrlIfNew();
      setAnalyticsState(prev => ({
        ...prev,
        focusCount: focusCountRef.current,
        pageViews: newUrl ? pageViewsRef.current : prev.pageViews,
        lastActivity: now,
      }));
    };

    const handleWindowFocus = () => {
      const now = Date.now();
      focusCountRef.current += 1;
      lastActivityRef.current = now;
      const newUrl = recordUrlIfNew();
      setAnalyticsState(prev => ({
        ...prev,
        focusCount: focusCountRef.current,
        pageViews: newUrl ? pageViewsRef.current : prev.pageViews,
        lastActivity: now,
      }));
    };

    const handlePopState = () => {
      if (!recordUrlIfNew()) return;
      setAnalyticsState(prev => ({ ...prev, pageViews: pageViewsRef.current }));
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [recordUrlIfNew]);

  const deliveryRef = useRef<(item: QueuedEvent) => Promise<void>>(async () => {});

  const drainQueue = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    const queue = queueRef.current;
    if (queue.length === 0) return;
    const now = Date.now();
    const ready: QueuedEvent[] = [];
    const remaining: QueuedEvent[] = [];
    queue.forEach(item => {
      if (item.nextAttemptAt <= now) ready.push(item);
      else remaining.push(item);
    });
    queueRef.current = remaining;

    ready.forEach(item => {
      void deliveryRef.current(item);
    });

    if (queueRef.current.length > 0) {
      const next = queueRef.current.reduce(
        (acc, item) => Math.min(acc, item.nextAttemptAt),
        Number.POSITIVE_INFINITY
      );
      const delay = Math.max(50, next - Date.now());
      retryTimerRef.current = setTimeout(drainQueue, delay);
    }
  }, []);

  const enqueueRetry = useCallback(
    (item: QueuedEvent) => {
      if (item.attempts >= MAX_RETRY_ATTEMPTS) return;
      const queue = queueRef.current;
      if (queue.length >= MAX_QUEUE_SIZE) {
        queue.shift();
      }
      const nextAttempt = item.attempts + 1;
      const backoff = RETRY_BASE_DELAY_MS * Math.pow(2, item.attempts);
      queue.push({
        ...item,
        attempts: nextAttempt,
        nextAttemptAt: Date.now() + backoff,
      });
      if (!retryTimerRef.current) {
        retryTimerRef.current = setTimeout(drainQueue, backoff);
      }
    },
    [drainQueue]
  );

  const attemptDelivery = useCallback(
    async (item: QueuedEvent) => {
      try {
        const res = await fetch(item.endpoint, {
          method: 'POST',
          headers: item.headers,
          body: item.body,
          keepalive: true,
        });
        if (!res.ok && res.status >= 500) {
          enqueueRetry(item);
        }
      } catch (error) {
        console.warn('Analytics error:', error);
        enqueueRetry(item);
      }
    },
    [enqueueRetry]
  );

  useEffect(() => {
    deliveryRef.current = attemptDelivery;
  }, [attemptDelivery]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const buildPayload = useCallback(
    (action: string, data: Record<string, unknown>) => {
      const state = analyticsStateRef.current;
      const now = Date.now();
      const eventIndex = eventIndexRef.current + 1;
      const eventId =
        typeof data.eventId === 'string' && data.eventId.trim()
          ? data.eventId.trim()
          : createAnalyticsEventId(action, state.sessionId, eventIndex);
      const idempotencyKey =
        typeof data.idempotencyKey === 'string' && data.idempotencyKey.trim()
          ? data.idempotencyKey.trim()
          : eventId;
      eventIndexRef.current = eventIndex;
      setAnalyticsState(prev => ({
        ...prev,
        eventsLogged: eventIndex,
        lastActivity: now,
      }));
      lastActivityRef.current = now;

      // W3 (docs/contracts/chat-widget.md): sendBeacon cannot set headers, so
      // tokenless beacon events were dropped platform-side. The platform accepts
      // the widget token in the BODY (analytics.js explicitToken) — include it.
      const bodyToken = analyticsApiKey || apiKey || null;

      return {
        ...(bodyToken ? { token: bodyToken } : {}),
        action,
        eventId,
        idempotencyKey,
        sessionId: state.sessionId,
        session_id: state.sessionId,
        orgId: analyticsContext?.orgId,
        agentId: analyticsContext?.agentId,
        channel: analyticsContext?.channel,
        provider: analyticsContext?.provider,
        threadId: (data.threadId as unknown) || undefined,
        chat_id: (data.chatId as unknown) || (data.threadId as unknown) || undefined,
        conversationId:
          (data.conversationId as unknown) ||
          (data.threadId as unknown) ||
          (data.ticketId as unknown) ||
          undefined,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        data: {
          ...analyticsContext,
          ...data,
          eventId,
          idempotencyKey,
          sessionId: state.sessionId,
          sessionDuration: now - state.sessionStartTime,
          pageViews: pageViewsRef.current,
          focusCount: focusCountRef.current,
          eventIndex,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        },
        timestamp: new Date().toISOString(),
        customFields,
      };
    },
    [analyticsContext, analyticsApiKey, apiKey, customFields]
  );

  const sendEvent = useCallback(
    (
      action: string,
      data: Record<string, unknown> = {},
      transport: AnalyticsTransport = 'fetch'
    ) => {
      if (analyticsEnabled === false) return;
      if (sampleRef.current === false && !isImportantAnalyticsEvent(action, data)) return;

      const payload = buildPayload(action, data);

      notifyHost(onAnalyticsEventRef.current, { action, payload, transport });

      if (!analyticsBaseUrl) return;
      const endpoint = resolveEndpoint();
      if (!endpoint) return;

      const body = JSON.stringify(payload);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        // Bearer only: the response.stateset.com ingest's CORS allows Authorization but not
        // x-api-key, so sending both made browsers block every analytics request.
        ...(analyticsApiKey || apiKey
          ? { Authorization: `Bearer ${analyticsApiKey || apiKey}` }
          : {}),
      };

      if (transport === 'beacon' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        try {
          const blob = new Blob([body], { type: 'application/json' });
          const ok = navigator.sendBeacon(endpoint, blob);
          if (ok) return;
        } catch {
          // fall through to fetch
        }
      }

      void attemptDelivery({
        endpoint,
        body,
        headers,
        attempts: 0,
        nextAttemptAt: Date.now(),
      });
    },
    [
      analyticsBaseUrl,
      analyticsEnabled,
      analyticsApiKey,
      apiKey,
      attemptDelivery,
      buildPayload,
      resolveEndpoint,
    ]
  );

  const trackAnalytics = useCallback(
    (
      action: string,
      data: Record<string, unknown> = {},
      transport: AnalyticsTransport = 'fetch'
    ) => {
      try {
        sendEvent(action, data, transport);
      } catch {
        // Analytics is best-effort, including host data that cannot be serialized.
      }
    },
    [sendEvent]
  );

  return trackAnalytics;
}

export function useEnhancedAnalytics({
  analyticsBaseUrl,
  apiKey,
  customFields,
  analyticsContext,
  analyticsEnabled,
  analyticsSampleRate,
  analyticsEndpoint,
  analyticsApiKey,
  sessionConfig,
  onAnalyticsEvent,
}: UseAnalyticsOptions) {
  const trackAnalytics = useAnalytics({
    analyticsBaseUrl,
    apiKey,
    customFields,
    analyticsContext,
    analyticsEnabled,
    analyticsSampleRate,
    analyticsEndpoint,
    analyticsApiKey,
    sessionConfig,
    onAnalyticsEvent,
  });
  const [startTime, setStartTime] = useState<number>(Date.now());

  return {
    track: trackAnalytics,

    trackSession: async (data?: Record<string, unknown>) => {
      await trackAnalytics('session_start', {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },

    trackMessage: async (messageType: 'user' | 'assistant', content: string, extra?: EventData) => {
      await trackAnalytics('message', {
        messageType,
        characterCount: content.length,
        ...extra,
      });
    },

    trackToolCall: async (
      toolName: string,
      status: 'started' | 'succeeded' | 'failed',
      duration?: number
    ) => {
      await trackAnalytics('tool_call', {
        toolName,
        status,
        duration,
      });
    },

    trackCitation: async (source: string, confidence?: number) => {
      await trackAnalytics('citation', {
        source,
        confidence,
      });
    },

    trackError: async (error: string, context?: Record<string, unknown>) => {
      await trackAnalytics('error', {
        error,
        ...context,
      });
    },

    trackExport: async (format: string, messageCount: number) => {
      await trackAnalytics('export', {
        format,
        messageCount,
      });
    },

    trackFileUpload: async (fileName: string, fileSize: number, status: 'success' | 'failed') => {
      await trackAnalytics('file_upload', {
        fileName,
        fileSize,
        status,
      });
    },

    trackViewChange: async (fromView: string, toView: string) => {
      await trackAnalytics('view_change', {
        fromView,
        toView,
      });
    },

    trackConversationEnd: async (totalMessages: number, satisfaction?: number) => {
      const duration = Date.now() - startTime;
      setStartTime(Date.now());

      await trackAnalytics('conversation_end', {
        totalMessages,
        duration,
        satisfaction,
      });
    },

    resetTimer: () => {
      setStartTime(Date.now());
    },

    getDuration: () => {
      return Date.now() - startTime;
    },
  };
}
